/**
 * <sanneway-slider> — full-height Swiper (fade) driven by a pill-accordion overlay.
 *
 * - Each pill maps 1:1 to a slide. The active slide's pill is open (others closed); the
 *   background image reveals via the `swiper-slide-active` class (CSS handles the animation).
 * - Clicking a different pill activates that slide; clicking the already-active pill toggles
 *   its accordion open/closed. Prev/next arrows step through slides (Swiper navigation).
 *
 * Swiper is loaded eagerly by the section (self-hosted bundle); we poll for `window.Swiper`
 * with setTimeout (rAF is throttled in background tabs — see cart-upsell.js).
 */

class SannewaySlider extends HTMLElement {
  connectedCallback() {
    this.pills = Array.from(this.querySelectorAll('[data-sanneway-pill]'));
    this.slides = Array.from(this.querySelectorAll('.sanneway__slide'));
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.inView = true;
    this.mobileQuery.addEventListener?.('change', this.#onBreakpointChange);
    this.#observeViewport();
    this.addEventListener('click', this.#onClick);
    this.addEventListener('shopify:block:select', this.#onBlockSelect);
    this.#ensureSwiper().then(() => this.#initSwiper());
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('shopify:block:select', this.#onBlockSelect);
    this.mobileQuery?.removeEventListener?.('change', this.#onBreakpointChange);
    this.observer?.disconnect();
    this.#pauseAll();
    this.swiper?.destroy?.(true, true);
  }

  #onClick = (event) => {
    const toggle = event.target.closest('[data-sanneway-pill-toggle]');
    if (!toggle) return;
    const pill = toggle.closest('[data-sanneway-pill]');
    if (pill) this.#onPillClick(pill);
  };

  #onPillClick(pill) {
    const index = Number(pill.dataset.index);
    if (!this.swiper) return;
    if (index === this.swiper.activeIndex) {
      // Toggle the active pill's accordion without changing the slide.
      const open = pill.classList.toggle('is-open');
      pill.querySelector('[data-sanneway-pill-toggle]')?.setAttribute('aria-expanded', String(open));
    } else {
      this.swiper.slideTo(index);
    }
  }

  #syncPills(activeIndex) {
    for (const pill of this.pills) {
      const open = Number(pill.dataset.index) === activeIndex;
      pill.classList.toggle('is-open', open);
      pill.querySelector('[data-sanneway-pill-toggle]')?.setAttribute('aria-expanded', String(open));
    }
  }

  #onBlockSelect = (event) => {
    const slide = event.target?.closest?.('.sanneway__slide');
    const index = slide ? this.slides.indexOf(slide) : -1;
    if (index >= 0) this.swiper?.slideTo(index);
  };

  #onBreakpointChange = () => this.#syncVideos(this.swiper?.activeIndex ?? 0);

  #observeViewport() {
    if (!('IntersectionObserver' in window)) return;
    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.inView = entry.isIntersecting;
        this.#syncVideos(this.swiper?.activeIndex ?? 0);
      },
      { threshold: 0.1 }
    );
    this.observer.observe(this);
  }

  #activeVideo(slide) {
    const mobile = slide.querySelector('[data-sanneway-video="mobile"]');
    if (this.mobileQuery.matches && mobile) return mobile;
    return slide.querySelector('[data-sanneway-video="desktop"]');
  }

  #ensureSource(video) {
    if (video.dataset.src && !video.getAttribute('src')) {
      video.setAttribute('src', video.dataset.src);
      video.load();
    }
    return Boolean(video.getAttribute('src'));
  }

  #stop(video) {
    if (!video) return;
    video.pause();
    if (video.currentTime) video.currentTime = 0;
  }

  #pauseAll() {
    for (const video of this.querySelectorAll('[data-sanneway-video]')) video.pause();
  }

  #syncVideos(activeIndex) {
    if (!this.slides?.length) return;
    this.slides.forEach((slide, index) => {
      const wanted = index === activeIndex ? this.#activeVideo(slide) : null;
      for (const video of slide.querySelectorAll('[data-sanneway-video]')) {
        if (video !== wanted) this.#stop(video);
      }
      if (!wanted || !this.inView || this.reduceMotionQuery.matches) return;
      if (!this.#ensureSource(wanted)) return;
      wanted.muted = true;
      wanted.play()?.catch?.(() => {});
    });
  }

  #ensureSwiper() {
    return new Promise((resolve) => {
      const check = () => (window.Swiper ? resolve() : setTimeout(check, 50));
      check();
    });
  }

  #initSwiper() {
    const el = this.querySelector('[data-sanneway-swiper]');
    if (!el || !window.Swiper) return;
    this.swiper = new window.Swiper(el, {
      effect: 'fade',
      fadeEffect: { crossFade: true },
      speed: 900,
      slidesPerView: 1,
      allowTouchMove: true,
      navigation: {
        prevEl: this.querySelector('[data-sanneway-prev]'),
        nextEl: this.querySelector('[data-sanneway-next]'),
      },
      on: {
        init: (sw) => {
          this.#syncPills(sw.activeIndex);
          this.#syncVideos(sw.activeIndex);
        },
        slideChange: (sw) => {
          this.#syncPills(sw.activeIndex);
          this.#syncVideos(sw.activeIndex);
        },
      },
    });
  }
}

if (!customElements.get('sanneway-slider')) {
  customElements.define('sanneway-slider', SannewaySlider);
}
